import { useMemo } from 'react';

import type { CompanyMetrics } from './useCompetitorComposition';
import type { Competitor } from './useCompetitors';
import {
  type CompanySentiment,
  SENTIMENT_LABELS,
  type SentimentScores,
} from './useSentimentComparison';

export interface HiddenGem {
  companyId: number;
  shortName: string;
  longName: string;
  score: number; // 0–100 composite score
  highlights: string[];
  salary: number | null;
  sentimentOverall: number | null;
  headcount: number | null;
  linkedInUrl: string | null;
}

interface UseHiddenGemsResult {
  hiddenGems: HiddenGem[];
}

const percentileRank = (value: number, sortedValues: number[]): number => {
  if (sortedValues.length === 0) return 50;
  const belowCount = sortedValues.filter((v) => v < value).length;
  return (belowCount / sortedValues.length) * 100;
};

const getTopSentimentHighlights = (
  scores: SentimentScores,
  maxHighlights: number
): string[] => {
  const highlights: Array<{ key: keyof SentimentScores; value: number }> = [];

  for (const [key, value] of Object.entries(scores)) {
    if (key === 'overall') continue;
    // Values are on 0–1 scale; 0.7 (70%) is a strong signal
    if (typeof value === 'number' && value > 0.7) {
      highlights.push({ key: key as keyof SentimentScores, value });
    }
  }

  return highlights
    .sort((a, b) => b.value - a.value)
    .slice(0, maxHighlights)
    .map(
      (h) => `Strong ${SENTIMENT_LABELS[h.key]} (${(h.value * 5).toFixed(1)}/5)`
    );
};

/**
 * Derives "hidden gem" competitor companies — those with strong pay and
 * culture metrics that aren't big-brand names.
 *
 * @param excludeCompanyIds - Set of company IDs to exclude entirely
 *   (e.g. user's previous employers). These must not appear in results.
 */
export const useHiddenGems = (
  metrics: CompanyMetrics[],
  sentiments: CompanySentiment[],
  competitors: Competitor[],
  userCompanyId: number | null,
  excludeCompanyIds?: Set<number>
): UseHiddenGemsResult => {
  const hiddenGems = useMemo((): HiddenGem[] => {
    if (metrics.length < 2) return [];

    const sentimentMap = new Map(sentiments.map((s) => [s.companyId, s]));
    const linkedInMap = new Map(competitors.map((c) => [c.id, c.linkedInUrl]));

    // Calculate medians for salary, sentiment, and headcount
    const salaries = metrics
      .map((m) => m.salary)
      .filter((s): s is number => s !== null);
    const headcounts = metrics
      .map((m) => m.headcount)
      .filter((h): h is number => h !== null)
      .sort((a, b) => a - b);
    const sentimentOveralls = sentiments
      .map((s) => s.scores.overall)
      .filter((v): v is number => v !== null);

    const medianSalary =
      salaries.length > 0
        ? (salaries.sort((a, b) => a - b)[Math.floor(salaries.length / 2)] ?? 0)
        : 0;
    const medianSentiment =
      sentimentOveralls.length > 0
        ? (sentimentOveralls.sort((a, b) => a - b)[
            Math.floor(sentimentOveralls.length / 2)
          ] ?? 0)
        : 0;

    // Top 3 by headcount are "big brands"
    const bigBrandIds = new Set(
      [...headcounts]
        .sort((a, b) => b - a)
        .slice(0, 3)
        .map((hc) => metrics.find((m) => m.headcount === hc)?.companyId)
        .filter((id): id is number => id !== undefined)
    );

    const sortedSalaries = [...salaries].sort((a, b) => a - b);
    const sortedSentiments = [...sentimentOveralls].sort((a, b) => a - b);

    const candidates = metrics
      .filter((m) => {
        // Exclude user's current/source company
        if (m.companyId === userCompanyId) return false;
        // Exclude big brands
        if (bigBrandIds.has(m.companyId)) return false;
        // Exclude user's previous employers (all historical companies)
        if (excludeCompanyIds?.has(m.companyId)) return false;

        const salary = m.salary ?? 0;
        const sentiment = sentimentMap.get(m.companyId)?.scores.overall ?? 0;

        return salary >= medianSalary && sentiment >= medianSentiment;
      })
      .map((m) => {
        const sentiment = sentimentMap.get(m.companyId);
        const salaryPctile = percentileRank(m.salary ?? 0, sortedSalaries);
        const sentimentPctile = percentileRank(
          sentiment?.scores.overall ?? 0,
          sortedSentiments
        );
        // Values are on 0–1 scale (0.5 = neutral, 0.8 = strong)
        const wlbScore = sentiment?.scores.work_life_balance ?? 0.5;
        const careerScore = sentiment?.scores.career_growth ?? 0.5;

        // Composite: salary 40%, overall sentiment 30%, WLB 20%, career growth 10%
        const score =
          salaryPctile * 0.4 +
          sentimentPctile * 0.3 +
          wlbScore * 100 * 0.2 +
          careerScore * 100 * 0.1;

        const highlights: string[] = [];

        if (salaryPctile >= 70) {
          highlights.push(
            `Top ${Math.round(100 - salaryPctile)}% pay among peers`
          );
        }

        if (sentiment) {
          highlights.push(...getTopSentimentHighlights(sentiment.scores, 2));
        }

        if (m.growthTrend === 'growing') {
          highlights.push('Growing workforce');
        }

        return {
          companyId: m.companyId,
          shortName: m.shortName,
          longName: m.longName,
          score: Math.round(score),
          highlights: highlights.slice(0, 3),
          salary: m.salary,
          sentimentOverall: sentiment?.scores.overall ?? null,
          headcount: m.headcount,
          linkedInUrl: linkedInMap.get(m.companyId) ?? null,
        };
      })
      .sort((a, b) => b.score - a.score);

    return candidates.slice(0, 3);
  }, [metrics, sentiments, competitors, userCompanyId, excludeCompanyIds]);

  return { hiddenGems };
};
